import { defineStore } from 'pinia';
import * as mediaApi from '@/api/mediaApi';

/**
 * Page state for the media prototype: who is looking, which patient (or the personal library), the
 * config lists, and a `version` that every write bumps so the open layout reloads. Data itself is
 * never cached here: layouts ask `@/api/mediaApi` (through `run`) and render what it answers.
 */
export const useMediaStore = defineStore('media', {
  state: () => ({
    viewer: mediaApi.getViewer(),
    patients: [],
    // A patient's unique_ID, or null for the personal library.
    patientUid: null,
    lastPatientUid: null,
    // The patient of the chart that opened the page, kept so a reset adds them back.
    chartPatient: null,
    config: { primary_tags: [], body_sites: [], settings: {}, people: {} },
    version: 0,
    toast: null,
    ready: false,
  }),
  getters: {
    canManage: (s) => !!s.viewer.can_manage,
    inLibrary: (s) => s.patientUid === null,
    patient: (s) => s.patients.find((p) => p.unique_ID === s.patientUid) || null,
    primaryTagLabel: (s) => (code) => (s.config.primary_tags.find((t) => t.code === code) || {}).label || code || '',
    bodySiteLabel: (s) => (code) => (s.config.body_sites.find((t) => t.code === code) || {}).label || code || '',
    personName: (s) => (uid) => s.config.people[uid] || uid,
  },
  actions: {
    /** `chartPatient`: the patient of the chart that opened the page, added to the roster first. */
    async init(patientUid, chartPatient = null) {
      if (chartPatient) this.chartPatient = chartPatient;
      if (this.chartPatient) await mediaApi.addMediaPatient(this.chartPatient);
      this.config = await mediaApi.loadMediaConfig();
      this.patients = await mediaApi.listMediaPatients();
      const wanted = this.patients.find((p) => p.unique_ID === patientUid);
      this.patientUid = wanted ? wanted.unique_ID : (this.patients[0] || {}).unique_ID || null;
      this.lastPatientUid = this.patientUid;
      this.ready = true;
    },
    setPatient(uid) {
      this.patientUid = uid;
      if (uid) this.lastPatientUid = uid;
    },
    async setViewer(kind) {
      this.viewer = mediaApi.setViewer(kind);
      await this.init(kind === 'patient' ? this.viewer.unique_ID : this.lastPatientUid);
      this.bump();
    },
    bump() { this.version += 1; },
    say(text, tone = 'ok') {
      this.toast = { text, tone, at: Date.now() };
      const at = this.toast.at;
      setTimeout(() => { if (this.toast && this.toast.at === at) this.toast = null; }, tone === 'error' ? 7000 : 3500);
    },
    /** Runs one mediaApi call; a refusal becomes a toast and resolves to null. */
    async run(fn, { success, bump = false } = {}) {
      try {
        const out = await fn(mediaApi);
        if (bump) this.bump();
        if (success) this.say(typeof success === 'function' ? success(out) : success);
        return out;
      } catch (e) {
        this.say(e && e.message ? e.message : 'Something went wrong.', 'error');
        return null;
      }
    },
    async reset() {
      await mediaApi.resetLocal();
      await this.init(this.lastPatientUid);
      this.bump();
      this.say('Local media data reset to the sample.');
    },
  },
});
